import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import CTAButton from './CTAButton';
import { motion, AnimatePresence } from 'framer-motion';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  index: number;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div 
      className="border border-gray-100 rounded-lg mb-4 overflow-hidden shadow-md hover:shadow-lg transition-shadow bg-white"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
    >
      <motion.button 
        className={`flex justify-between items-center w-full text-left font-heading font-semibold text-lg p-5 ${isOpen ? 'bg-accent/5 text-accent' : 'bg-white text-primary'}`}
        onClick={onClick}
        whileHover={{ backgroundColor: isOpen ? 'rgba(234, 88, 12, 0.1)' : 'rgba(243, 244, 246, 1)' }}
        whileTap={{ scale: 0.99 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex items-center">
          <HelpCircle size={18} className={`mr-3 ${isOpen ? 'text-accent' : 'text-gray-400'}`} />
          {question}
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <ChevronDown className={`flex-shrink-0 ${isOpen ? 'text-accent' : 'text-gray-400'}`} />
        </motion.div>
      </motion.button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-0 border-t border-gray-100">
              <motion.p 
                className="text-gray-600 leading-relaxed"
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.2, delay: 0.1 }}
              >
                {answer}
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  
  const faqItems = [
    {
      question: "Por que minha conta foi bloqueada pela Justiça?",
      answer: "O bloqueio de valores em conta acontece em razão de uma decisão judicial em um processo de execução de dívida, que pode ser cível, trabalhista ou até mesmo fiscal (tributos)."
    },
    {
      question: "Eu consigo recuperar os valores rapidamente?",
      answer: "Sim, com a interposição de recurso é possível desbloquear o valor na conta, principalmente nos casos de conta salário ou poupança."
    },
    {
      question: "O bloqueio dos valores acontece apenas uma vez?",
      answer: "Não! Caso você não se manifeste, o juiz determinará que ocorra a tentativa de bloqueio diariamente durante 30 dias até bloquear o valor total devido no processo."
    },
    {
      question: "Como desbloquear a minha conta?",
      answer: "O pedido de desbloqueio deve ser feito por um advogado no processo pelo qual a ordem de bloqueio foi determinada."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50" id="faq">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div 
          className="max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary relative inline-block">
                DÚVIDAS FREQUENTES
                <motion.div 
                  className="absolute -bottom-2 left-0 right-0 h-1 bg-accent rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: '100%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                />
              </h2>
            </motion.div>
            <motion.p 
              className="text-gray-500 mt-4 max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Tire suas principais dúvidas sobre o processo de desbloqueio de valores
            </motion.p>
          </div>
          
          <div className="space-y-2">
            {faqItems.map((item, index) => (
              <FAQItem 
                key={index}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === index}
                onClick={() => toggleFAQ(index)}
                index={index}
              />
            ))}
          </div>
          
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <CTAButton />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;